// Layout primitives: every container in the app is a Column or a Row with token gaps and padding,
// never a bare <div>. A Screen is the vertical rhythm of one page inside the AppShell; Rise,
// ScrollRow and VisuallyHidden are the three behaviours a plain container cannot express.
import "./config";
import { Text, View, XStack, YStack, styled } from "tamagui";

export const Column = styled(YStack, { name: "Column" });
export const Row = styled(XStack, {
  name: "Row",
  alignItems: "center",
  variants: {
    wrap: { true: { flexWrap: "wrap" } },
    spread: { true: { justifyContent: "space-between" } },
  } as const,
});

/** Fills the space left over in a Row or Column. */
export const Fill = styled(View, { name: "Fill", flex: 1, minWidth: 0 });

/** A hairline rule; the accessible version draws it as a visible 2px line. */
export const Divider = styled(View, {
  name: "Divider",
  render: "hr",
  height: "var(--borderW)",
  alignSelf: "stretch",
  backgroundColor: { default: "line", a11yLight: "lineStrong", a11yDark: "lineStrong" },
  borderWidth: 0,
  margin: 0,
});

/** The page column inside the AppShell: one vertical rhythm for every screen. */
export const Screen = styled(YStack, {
  name: "Screen",
  gap: "5",
  flexGrow: 1,
  flexShrink: 0,
  flexBasis: "auto",
});

export type ColumnProps = React.ComponentProps<typeof Column>;
export type RowProps = React.ComponentProps<typeof Row>;

/** A row that scrolls sideways instead of wrapping (chips, tiles, cards); the scrollbar stays hidden. */
export function ScrollRow({ children, ...rest }: RowProps): React.JSX.Element {
  return (
    // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
    <Row className="ui-scroll-x" gap="2" {...rest}>
      {children}
    </Row>
  );
}

export type RiseProps = ColumnProps & {
  /** Position in a list: later items start a little later, capped so a long list does not crawl. */
  index?: number;
};

/** Mounts with a short rise and fade. Reduced motion and the accessible version turn it off in global.css. */
export function Rise({ index = 0, children, ...rest }: RiseProps): React.JSX.Element {
  return (
    <Column
      className="ui-rise"
      style={{ animationDelay: `${String(Math.min(index, 6) * 45)}ms` }}
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...rest}
    >
      {children}
    </Column>
  );
}

/** Text only assistive technology reads: a live-region announcement, a longer name for a figure. */
export const VisuallyHidden = styled(Text, {
  name: "VisuallyHidden",
  render: "span",
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  whiteSpace: "nowrap",
  borderWidth: 0,
  clipPath: "inset(50%)",
});

/** Content that is on screen but not on paper (a print screen's buttons). */
export function NoPrint({ children, ...rest }: ColumnProps): React.JSX.Element {
  return (
    // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
    <Column className="ui-no-print" {...rest}>
      {children}
    </Column>
  );
}
